import React, { forwardRef } from 'react';
import type { ScrollViewProps, ViewProps } from 'react-native';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useDynamicStyles } from '../../hooks/useDynamicStyles';

type TableProps = ViewProps & {
  children: React.ReactNode;
};

type TableScrollContainerProps = ScrollViewProps & {
  children: React.ReactNode;
};

/**
 * Table - Root table container
 *
 * Wraps table content. Supports loading states, empty states, and clickable rows.
 * Use with TableScrollContainer for horizontal overflow handling.
 *
 * @example Complete table with all features
 * ```tsx
 * <TableScrollContainer>
 *   <Table>
 *     <TableHeader>
 *       <TableHead flex={2}>Name</TableHead>
 *       <TableHead flex={3}>Email</TableHead>
 *       <TableHead flex={1}>Role</TableHead>
 *     </TableHeader>
 *     <TableBody
 *       isLoading={isLoading}
 *       isEmpty={data.length === 0}
 *       loadingRows={5}
 *       loadingContent={
 *         <TableRow>
 *           <TableCell flex={2}><TableSkeletonLoader style={styles.skeleton} /></TableCell>
 *           <TableCell flex={3}><TableSkeletonLoader style={styles.skeleton} /></TableCell>
 *           <TableCell flex={1}><TableSkeletonLoader style={styles.skeleton} /></TableCell>
 *         </TableRow>
 *       }
 *       emptyContent={<TextReg>No data found</TextReg>}
 *     >
 *       {data.map(item => (
 *         <TableRow key={item.id} onPress={() => handleRowClick(item)}>
 *           <TableCell flex={2}>{item.name}</TableCell>
 *           <TableCell flex={3}>{item.email}</TableCell>
 *           <TableCell flex={1}>{item.role}</TableCell>
 *         </TableRow>
 *       ))}
 *     </TableBody>
 *   </Table>
 * </TableScrollContainer>
 * ```
 */
export const Table = forwardRef<View, TableProps>(
  ({ children, style, ...props }, ref) => {
    const styles = useDynamicStyles(createStyles);

    return (
      <View ref={ref} style={[styles.container, style]} {...props}>
        {children}
      </View>
    );
  },
);

Table.displayName = 'Table';

/**
 * TableScrollContainer - Horizontal scroll wrapper for tables
 *
 * Enables horizontal scrolling for wide tables. Recommended for tables with many columns.
 *
 * @example
 * ```tsx
 * <TableScrollContainer>
 *   <Table>
 *     <TableHeader>
 *       <TableHead flex={2}>Name</TableHead>
 *       <TableHead flex={3}>Email</TableHead>
 *     </TableHeader>
 *     <TableBody>
 *       {data.map(item => (
 *         <TableRow key={item.id} onPress={() => handleRowClick(item)}>
 *           <TableCell flex={2}>{item.name}</TableCell>
 *           <TableCell flex={3}>{item.email}</TableCell>
 *         </TableRow>
 *       ))}
 *     </TableBody>
 *   </Table>
 * </TableScrollContainer>
 * ```
 */
export const TableScrollContainer = forwardRef<
  ScrollView,
  TableScrollContainerProps
>(({ children, style, contentContainerStyle, ...props }, ref) => {
  return (
    <ScrollView
      ref={ref}
      horizontal
      showsHorizontalScrollIndicator={true}
      style={[{ flex: 1 }, style]}
      contentContainerStyle={[{ flexGrow: 1 }, contentContainerStyle]}
      {...props}
    >
      {children}
    </ScrollView>
  );
});

TableScrollContainer.displayName = 'TableScrollContainer';

const createStyles = () =>
  StyleSheet.create({
    container: {
      width: '100%',
      overflow: 'hidden',
    },
  });
